import type { OfflineDeclaration, OfflineDependency } from '@nexus/core';
import { StatusBadge } from '@nexus/ui';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../api/client';
import type { EffectiveSettings, SessionInfo } from '../api/types';
import { useModules } from '../modules/context';
import { canIn, sessionQuery, useSession } from '../session/session';
import { settingsQuery } from '../session/settings';
import { useOnline } from '../sync/sync-status';
import {
  OfflineEngine,
  OfflineUnavailableError,
  restoreSnapshot,
  type LocalRecord,
  type OfflineChange,
  type OfflineState,
  type OfflineUpload,
  type QueuedFile,
} from './engine';
import { deleteStore, knownDatabase, lastIdentity } from './store';

/** Pull and push in the background while the app is open. */
const SYNC_INTERVAL_MS = 5 * 60_000;

const OfflineContext = createContext<OfflineEngine | null>(null);

/** The engine of the account and company the app shows, for calls outside of components. */
let active: OfflineEngine | null = null;

const EMPTY_STATE: OfflineState = {
  ready: true,
  syncing: false,
  lastSync: null,
  entities: [],
  changes: [],
  records: new Map(),
  prepared: [],
  uploads: [],
  storage: { files: 0, bytes: 0, full: false },
};
const NONE: readonly LocalRecord[] = [];
const ignore = () => undefined;

/**
 * Runs the offline sync for the active account and company (ADR-0013) when an active module
 * declares entities the person may take offline. Without one nothing is stored on the device.
 */
export function OfflineProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const modules = useModules();
  const client = useQueryClient();
  const online = useOnline();
  const accountId = session.account.id;
  const companyId = session.company?.id;
  const declared = useMemo(
    () =>
      modules
        .filter((m) => session.modules.includes(m.manifest.id))
        .flatMap((m) => m.manifest.offline ?? [])
        .some((d: OfflineDeclaration) => canIn(session, d.permissions.read)),
    [modules, session],
  );
  const engine = useMemo(() => {
    if (!companyId || !declared) return null;
    const next = new OfflineEngine({ accountId, companyId }, api);
    next.snapshot = () => ({
      session: client.getQueryData(sessionQuery.queryKey),
      settings: client.getQueryData(settingsQuery.queryKey),
    });
    return next;
  }, [accountId, companyId, declared, client]);

  useEffect(() => {
    if (!companyId) return;
    if (!engine) {
      // Rights or modules changed: what the device still holds goes.
      const identity = { accountId, companyId };
      if (knownDatabase(identity)) void deleteStore(identity);
      return;
    }
    engine.resume();
    active = engine;
    void engine.start().catch(ignore);
    const timer = setInterval(() => {
      if (navigator.onLine) void engine.sync().catch(ignore);
    }, SYNC_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      engine.pause();
      if (active === engine) active = null;
    };
  }, [accountId, companyId, engine]);

  useEffect(() => {
    if (online && engine) void engine.sync().catch(ignore);
  }, [online, engine]);

  return <OfflineContext.Provider value={engine}>{children}</OfflineContext.Provider>;
}

/** The running engine, `null` without offline entities. For the sync panel. */
export function useOfflineEngine(): OfflineEngine | null {
  return useContext(OfflineContext);
}

const noSubscription = () => ignore;
const emptyState = () => EMPTY_STATE;

export function useOfflineState(): OfflineState {
  const engine = useOfflineEngine();
  return useSyncExternalStore(
    engine?.subscribe ?? noSubscription,
    engine?.getState ?? emptyState,
    emptyState,
  );
}

/**
 * Records of an offline entity on the device, with the person's unsent changes on top.
 * `available` is false while the entity is not (yet) on the device.
 */
export function useOfflineRecords(entity: string): {
  records: readonly LocalRecord[];
  available: boolean;
  ready: boolean;
} {
  const state = useOfflineState();
  const info = state.entities.find((e) => e.entity === entity);
  return {
    records: state.records.get(entity) ?? NONE,
    available: !!info?.lastSync,
    ready: state.ready,
  };
}

export function useOfflineRecord(entity: string, id: string): LocalRecord | undefined {
  return useOfflineRecords(entity).records.find((r) => r.id === id);
}

export interface OfflineMutationOptions {
  /**
   * Records whose creation must reach the server first, e.g. the inspection of a new finding or
   * a file of `queueUpload` (`{ entity: 'core.files', recordId: fileId }`). The device holds the
   * change back until then.
   */
  dependsOn?: readonly OfflineDependency[];
}

/**
 * Changes to an offline entity that work without network: stored on the device and sent with
 * the next sync. The server checks them again (permission, scope, conflicts).
 */
export function useOfflineMutation(entity: string) {
  const engine = useOfflineEngine();
  const state = useOfflineState();
  const writable = !!state.entities.find((e) => e.entity === entity)?.writable;
  return useMemo(() => {
    const run = async (
      type: 'create' | 'update' | 'delete',
      id: string | null,
      changes: Record<string, unknown> = {},
      options: OfflineMutationOptions = {},
    ) => {
      if (!engine) throw new OfflineUnavailableError();
      const recordId = await engine.change(entity, type, id, changes, options);
      if (navigator.onLine) void engine.sync().catch(ignore);
      return recordId;
    };
    return {
      writable,
      /** Returns the ID of the new record; `id` (UUIDv7) when the app chose one. */
      create: (
        data: Record<string, unknown>,
        options: OfflineMutationOptions & { id?: string } = {},
      ) => run('create', options.id ?? null, data, options),
      update: async (
        id: string,
        changes: Record<string, unknown>,
        options?: OfflineMutationOptions,
      ) => {
        await run('update', id, changes, options);
      },
      remove: async (id: string, options?: OfflineMutationOptions) => {
        await run('delete', id, {}, options);
      },
    };
  }, [engine, entity, writable]);
}

/**
 * Where a record stands on the device: a change that conflicted or was refused first, else the
 * first change still waiting for the server, else `null`.
 */
export function useOfflineChange(entity: string, id: string): OfflineChange | null {
  const { changes } = useOfflineState();
  return useMemo(() => {
    const own = changes.filter((c) => c.entity === entity && c.recordId === id);
    return own.find((c) => c.state !== 'pending') ?? own[0] ?? null;
  }, [changes, entity, id]);
}

/** Pushes and pulls the offline data now, e.g. after saving offline. Without network: nothing. */
export async function syncNow(): Promise<void> {
  if (active && navigator.onLine) await active.sync().catch(ignore);
}

/**
 * Keeps a file on the device at once and uploads it with the next sync, once there is network and
 * the record and `dependsOn` reached the server (docs/architecture/offline.md, Dateien). Only for
 * records of entities the device keeps. Returns the file ID. Throws `offline-unavailable`, or
 * `device-storage-full` when the device has no room.
 */
export async function queueUpload(input: QueuedFile): Promise<string> {
  if (!active) throw new OfflineUnavailableError();
  const fileId = await active.queueUpload(input);
  if (navigator.onLine) void active.sync().catch(ignore);
  return fileId;
}

/**
 * Content of a file the device keeps (prepared records, waiting uploads) for code outside of
 * components, e.g. a query function; `null` when the device does not hold it.
 */
export async function fileOnDevice(fileId: string): Promise<Blob | null> {
  return (await active?.file(fileId)) ?? null;
}

/** Files waiting for their upload or refused, e.g. of one record. */
export function useUploads(
  filter: { entity?: string; recordId?: string } = {},
): readonly OfflineUpload[] {
  const { uploads } = useOfflineState();
  const { entity, recordId } = filter;
  return useMemo(
    () =>
      uploads.filter(
        (u) => (!entity || u.entity === entity) && (!recordId || u.recordId === recordId),
      ),
    [uploads, entity, recordId],
  );
}

export interface OfflineFile {
  /** Object URL for `<img src>` or a download; `null` until `ready`. */
  url: string | null;
  blob: Blob | null;
  /** `missing`: neither on the device nor loadable without network; `failed`: the server refused. */
  status: 'loading' | 'ready' | 'missing' | 'failed';
}

const NO_FILE: OfflineFile = { url: null, blob: null, status: 'missing' };
const LOADING_FILE: OfflineFile = { url: null, blob: null, status: 'loading' };

async function loadFile(
  engine: OfflineEngine | null,
  fileId: string,
): Promise<Pick<OfflineFile, 'blob' | 'status'>> {
  try {
    const held = await engine?.file(fileId);
    if (held) return { blob: held, status: 'ready' };
  } catch {
    // Unreadable on the device: the server may still have it.
  }
  if (!navigator.onLine) return { blob: null, status: 'missing' };
  try {
    return { blob: await api.blob(`/files/${fileId}/content`), status: 'ready' };
  } catch (error) {
    return { blob: null, status: error instanceof ApiError ? 'failed' : 'missing' };
  }
}

/**
 * Content of a file: from the device when it keeps the file (prepared records, waiting uploads),
 * else from the server while there is network. A missing file loads once the connection is back.
 */
export function useOfflineFile(fileId: string | null | undefined): OfflineFile {
  const engine = useOfflineEngine();
  const onDevice = useSyncExternalStore(
    engine?.subscribe ?? noSubscription,
    () => !!fileId && !!engine?.hasFile(fileId),
    () => false,
  );
  const [loaded, setLoaded] = useState<{
    fileId: string;
    onDevice: boolean;
    file: OfflineFile;
  } | null>(null);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    let url: string | null = null;
    let status: OfflineFile['status'] = 'loading';
    const load = async () => {
      const result = await loadFile(engine, fileId);
      if (cancelled) return;
      if (url) URL.revokeObjectURL(url);
      url = result.blob ? URL.createObjectURL(result.blob) : null;
      status = result.status;
      setLoaded({ fileId, onDevice, file: { ...result, url } });
    };
    const reconnect = () => {
      if (status === 'missing' || status === 'failed') void load();
    };
    window.addEventListener('online', reconnect);
    void load();
    return () => {
      cancelled = true;
      window.removeEventListener('online', reconnect);
      if (url) URL.revokeObjectURL(url);
    };
  }, [engine, fileId, onDevice]);

  if (!fileId) return NO_FILE;
  if (loaded?.fileId !== fileId || loaded.onDevice !== onDevice) return LOADING_FILE;
  return loaded.file;
}

export interface OfflinePreparation {
  /** The device keeps the record and what belongs to it ready for working offline. */
  prepared: boolean;
  /** Prepared by the app because the person opened it (`useOfflineAutoPrepare`). */
  auto: boolean;
  preparing: boolean;
  /** What belongs to the record came to the device; `false` until a sync with network went through. */
  synced: boolean;
  /** Files of the record and of what belongs to it: on the device, and in all. */
  progress: { done: number; total: number };
  /** Bytes of those files on the device. */
  bytes: number;
  /** End of the last sync that went through, the state of the data on the device. */
  lastSync: Date | null;
  /** Preparing is possible: the device keeps the entity or records that point to it. */
  available: boolean;
  /** Prepares the record; one the app prepared by itself stays from now on until removed. */
  prepare: () => Promise<void>;
  /** Ends the preparation; the files leave the device. */
  remove: () => Promise<void>;
}

/**
 * Prepares a record for working offline, e.g. an object before a visit: the records that point
 * to it (`pins` in the manifest), their kernel data and all files come to the device now.
 */
export function useOfflinePrepared(entity: string, recordId: string): OfflinePreparation {
  const engine = useOfflineEngine();
  const state = useOfflineState();
  const entry = state.prepared.find((p) => p.entity === entity && p.recordId === recordId);
  const available =
    !!engine &&
    state.entities.some((e) => e.entity === entity || e.pins.some((p) => p.entity === entity));
  const prepared = !!entry;
  const auto = !!entry?.auto;
  const preparing = !!entry?.preparing;
  const synced = !!entry?.synced;
  const done = entry?.files.done ?? 0;
  const total = entry?.files.total ?? 0;
  const bytes = entry?.bytes ?? 0;
  const lastSync = state.lastSync;
  return useMemo(
    () => ({
      prepared,
      auto,
      preparing,
      synced,
      progress: { done, total },
      bytes,
      lastSync,
      available,
      prepare: async () => {
        if (!engine) throw new OfflineUnavailableError();
        await engine.prepare(entity, recordId);
      },
      remove: async () => {
        await engine?.unprepare(entity, recordId);
      },
    }),
    [
      engine,
      entity,
      recordId,
      prepared,
      auto,
      preparing,
      synced,
      done,
      total,
      bytes,
      lastSync,
      available,
    ],
  );
}

/**
 * Prepares a record by itself while `enabled`, e.g. an open inspection when the person opens it,
 * so it is on the device in the basement without a tap. The device keeps the most recently opened
 * ones for a while (`AUTO_PREPARED_LIMIT`, `AUTO_PREPARED_DAYS`); a record the person removed is
 * not prepared again by itself. Without network it waits for the next sync.
 */
export function useOfflineAutoPrepare(entity: string, recordId: string, enabled: boolean): void {
  const engine = useOfflineEngine();
  const { entities } = useOfflineState();
  // Only records of entities that can be prepared themselves (pin of the own entity by `id`).
  const preparable = entities.some(
    (e) => e.entity === entity && e.pins.some((p) => p.entity === entity),
  );
  useEffect(() => {
    if (!engine || !enabled || !preparable) return;
    void engine.prepare(entity, recordId, { auto: true }).catch(ignore);
  }, [engine, entity, recordId, enabled, preparable]);
}

/** Marks a record with changes that are not on the server yet (docs/ui/design-system.md). */
export function OfflinePendingBadge({ record }: { record: Pick<LocalRecord, 'pending'> }) {
  const { t } = useTranslation();
  if (!record.pending) return null;
  return (
    <StatusBadge status="info" icon="cloud-upload">
      {t('shell.sync.notSynced')}
    </StatusBadge>
  );
}

/**
 * Session and settings of the last sync, when the app starts without network and the device
 * holds offline data. The server checks every change again once the connection is back.
 */
export async function restoreOfflineSession(client: QueryClient): Promise<boolean> {
  const snapshot = await restoreSnapshot<{ session?: SessionInfo; settings?: EffectiveSettings }>(
    lastIdentity(),
  );
  if (!snapshot?.session || !snapshot.settings) return false;
  client.setQueryData(sessionQuery.queryKey, snapshot.session);
  client.setQueryData(settingsQuery.queryKey, snapshot.settings);
  return true;
}
